<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import StudioDataSheet from './StudioDataSheet.vue'
import { applyEdit, baseTable } from './charts/chartEdit.js'
import { deriveChartModel } from './charts/chartDerive.js'
import { stopChartEdit } from './charts/chartEditing.js'
import {
  addSeries,
  applyInputs,
  canAddSeries,
  canInsertRow,
  canRemoveSeries,
  commitText,
  dropInvalidColumn,
  editsData,
  insertRow,
  invalidKey,
  removeRow,
  removeSeries,
  seriesAt,
  setName,
  sheetColumns,
  sheetRows,
  shiftInvalid,
  textTargets,
} from './charts/chartSheet.js'

// RED-317 PR 3d-A — a Word chart edited in place on the Studio canvas. It
// mounts into the chart block (the node view's controller,
// charts/chartEditing.js): the data sheet slides open below the chart, and
// the chart's own texts (titles, axis titles, text boxes, legend entries,
// category labels) are edited where they are drawn. Every commit is one
// setChartEdit transaction (one undo step, autosaved as any other change);
// typing previews live. What does not parse stays in its cell, flagged,
// until fixed: it is never cleared and never applied, and "Valmis" waits for it.
// A row, series or pasted block past what the chart holds (MAX_POINTS, which
// the server enforces too) is refused and said so on the same status line.
const props = defineProps({
  editor: { type: Object, required: true },
  controller: { type: Object, required: true },
})

const { t } = useI18n()

// The node as the editor holds it now (undo/redo and autosave included).
const nodeState = shallowRef(props.controller.state.value)
const unsubscribe = props.controller.state.subscribe((value) => {
  nodeState.value = value
})

const served = computed(() => {
  void nodeState.value
  return props.controller.model()
})
const date1904 = computed(() => Boolean(served.value?.date1904))
const base = computed(() => baseTable(served.value))
const edit = computed(() => nodeState.value.node.attrs.chartEdit ?? null)
const table = computed(() => applyEdit(base.value, edit.value))

const labels = computed(() => ({ category: t('messages.editor.chart.category'), series: t('messages.editor.chart.series') }))
const messages = computed(() => ({ number: t('messages.editor.chart.invalidNumber'), date: t('messages.editor.chart.invalidDate') }))
const columns = computed(() => sheetColumns(table.value, labels.value))
const invalid = ref({})
const rows = computed(() => sheetRows(table.value, columns.value, invalid.value, messages.value, date1904.value))
const invalidCount = computed(() => Object.keys(invalid.value).length)
const linkedNote = computed(() => base.value?.workbook === 'external' && editsData(edit.value))

const sheet = useTemplateRef('sheet')
const active = ref({ row: 0, col: Math.max(0, columns.value.findIndex((column) => !column.leading)) })
const refusal = ref(false)
// The last row, series or paste was refused: the chart holds no more.
const full = ref(false)
const status = computed(() => {
  if (refusal.value && invalidCount.value) return t('messages.editor.chart.fixInvalid', { count: invalidCount.value })
  return full.value ? t('messages.editor.chart.full') : ''
})

// ------------------------------------------------------------------ commits

function commit(next) {
  const pos = props.controller.pos()
  if (typeof pos !== 'number' || JSON.stringify(next ?? null) === JSON.stringify(edit.value)) return
  full.value = false
  props.editor.commands.setChartEdit(pos, next)
}

function onDraft(draft) {
  if (!draft) {
    props.controller.preview(undefined)
    return
  }
  const result = applyInputs(base.value, edit.value, [draft], {}, date1904.value)
  props.controller.preview(Object.keys(result.invalid).length || result.tooLarge ? undefined : result.edit)
}

function onInput(inputs) {
  const result = applyInputs(base.value, edit.value, inputs, invalid.value, date1904.value)
  if (result.tooLarge) {
    full.value = true
    return
  }
  invalid.value = result.invalid
  if (!invalidCount.value) refusal.value = false
  if (result.changed) commit(result.edit)
}

function onHeader({ col, text }) {
  const column = columns.value[col]
  if (column && !column.leading) commit(setName(base.value, edit.value, column.idx, text))
}

const activeColumn = computed(() => columns.value[active.value.col] ?? null)
const canRemoveRow = computed(() => table.value.rows > 1 && active.value.row >= 0)
const canRemoveActiveSeries = computed(() => Boolean(activeColumn.value && !activeColumn.value.leading && canRemoveSeries(base.value, edit.value, activeColumn.value.idx)))

function addRowBelow() {
  if (!canInsertRow(base.value, edit.value)) {
    full.value = true
    return
  }
  const at = Math.min(Math.max(active.value.row, -1) + 1, table.value.rows)
  invalid.value = shiftInvalid(invalid.value, at, 1)
  commit(insertRow(base.value, edit.value, at))
  nextTick(() => sheet.value?.focusCell(at, active.value.col))
}

function removeActiveRow() {
  if (!canRemoveRow.value) return
  const at = active.value.row
  invalid.value = shiftInvalid(invalid.value, at, -1)
  commit(removeRow(base.value, edit.value, at))
  nextTick(() => sheet.value?.focusCell(Math.min(at, table.value.rows - 1), active.value.col))
}

function addSeriesAfter() {
  const after = seriesAt(table.value, activeColumn.value)
  if (after === null) return
  if (!canAddSeries(base.value, edit.value)) {
    full.value = true
    return
  }
  // A new series copies its neighbour's type, axis and colour rule; it gets a name of its own.
  const result = addSeries(base.value, edit.value, after)
  commit(setName(base.value, result.edit, result.idx, t('messages.editor.chart.newSeries', { n: table.value.series.length + 1 })))
  nextTick(() => {
    const col = columns.value.findIndex((column) => column.key === `s:${result.idx}`)
    if (col >= 0) sheet.value?.focusCell(Math.max(active.value.row, 0), col)
  })
}

function removeActiveSeries() {
  if (!canRemoveActiveSeries.value) return
  const column = activeColumn.value
  invalid.value = dropInvalidColumn(invalid.value, column.key)
  commit(removeSeries(base.value, edit.value, column.idx))
  nextTick(() => sheet.value?.focusCell(Math.max(active.value.row, 0), Math.min(active.value.col, columns.value.length - 1)))
}

const undo = () => props.editor.commands.undo()
const redo = () => props.editor.commands.redo()

// ----------------------------------------------------------------- the exit

const expanded = ref(false)
let closing = false

/** "Valmis" or a click outside: collapse, unless a typed text is still invalid. */
function done() {
  if (closing) return
  if (editingText.value) finishText(true)
  if (invalidCount.value) {
    refusal.value = true
    const [key] = Object.keys(invalid.value)
    const row = Number(key.slice(0, key.indexOf('|')))
    const col = columns.value.findIndex((column) => invalidKey(row, column.key) === key)
    sheet.value?.focusCell(row, Math.max(col, 0))
    return
  }
  closing = true
  expanded.value = false
  const finish = () => stopChartEdit(props.editor)
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) finish()
  else window.setTimeout(finish, 200)
}

// A press on the chart itself (not on one of its texts) keeps the keyboard in
// the sheet: the editor would otherwise take focus with the chart selected,
// and the next key typed would replace the chart.
function onChartPointer(event) {
  const host = props.controller.host()
  if (!(event.target instanceof Node) || host?.contains(event.target)) return
  event.preventDefault()
  sheet.value?.focus()
}

/** Esc in the sheet when nothing is being typed closes the chart's editor, not the drawer. */
function onPanelKeydown(event) {
  if (event.key !== 'Escape' || event.defaultPrevented) return
  event.preventDefault()
  event.stopPropagation()
  done()
}

function onOutsidePointer(event) {
  const block = props.controller.block()
  const target = event.target
  if (!(target instanceof Node) || block.contains(target)) return
  // PrimeVue's own floating layers (tooltips) belong to the editor.
  if (target instanceof Element && target.closest('.p-tooltip')) return
  done()
}

// --------------------------------------------------------- texts in place

const boxes = shallowRef([])
const editingText = ref(null) // { target, info, text, box }

const derived = computed(() => deriveChartModel(served.value, edit.value))
const targets = computed(() => (served.value ? textTargets(served.value, derived.value, table.value) : {}))

/** The drawn texts' boxes, grouped by edit target, relative to the chart block. */
function measure() {
  const block = props.controller.block()
  const svg = block.querySelector('svg.studio-chart-svg')
  if (!svg) {
    boxes.value = []
    return
  }
  const origin = block.getBoundingClientRect()
  const byTarget = new Map()
  for (const element of svg.querySelectorAll('[data-target]')) {
    const target = element.getAttribute('data-target')
    if (!targets.value[target]) continue
    const rect = element.getBoundingClientRect()
    if (!rect.width && !rect.height) continue
    const box = byTarget.get(target)
    if (!box) byTarget.set(target, { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom })
    else Object.assign(box, { left: Math.min(box.left, rect.left), top: Math.min(box.top, rect.top), right: Math.max(box.right, rect.right), bottom: Math.max(box.bottom, rect.bottom) })
  }
  boxes.value = [...byTarget.entries()].map(([target, box]) => ({
    target,
    left: box.left - origin.left - 2,
    top: box.top - origin.top - 1,
    width: box.right - box.left + 4,
    height: box.bottom - box.top + 2,
  }))
}

const boxStyle = (box) => ({ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` })

function openText(box) {
  const info = targets.value[box.target]
  if (!info) return
  if (editingText.value) finishText(true)
  editingText.value = { target: box.target, info, text: info.text, box }
  nextTick(() => {
    const field = textInput.value
    field?.focus()
    field?.select()
  })
}

const textInput = useTemplateRef('textInput')

function onTextInput(event) {
  editingText.value.text = event.target.value
  props.controller.preview(commitText(base.value, edit.value, editingText.value.info, editingText.value.text))
}

function finishText(save) {
  const current = editingText.value
  if (!current) return
  editingText.value = null
  props.controller.preview(undefined)
  if (save && current.text !== current.info.text) commit(commitText(base.value, edit.value, current.info, current.text))
}

function onTextKeydown(event) {
  if (event.key === 'Escape') {
    // Esc is the field's (the drawer would close on it).
    event.preventDefault()
    event.stopPropagation()
    finishText(false)
  } else if (event.key === 'Enter' && !(event.shiftKey && editingText.value?.info.multiline)) {
    event.preventDefault()
    finishText(true)
  } else if (event.key === 'Tab') {
    event.preventDefault()
    finishText(true)
    sheet.value?.focus()
  } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
    // Undo inside the field is the field's own.
    event.stopPropagation()
  }
}

const textFieldStyle = computed(() => {
  const box = editingText.value?.box
  if (!box) return {}
  const width = Math.max(box.width + 16, 120)
  return { left: `${box.left + box.width / 2 - width / 2}px`, top: `${box.top - 3}px`, width: `${width}px`, minHeight: `${Math.max(box.height + 6, 26)}px` }
})
const textRows = computed(() => Math.max(1, (editingText.value?.text ?? '').split('\n').length))

const targetLabel = (box) => t('messages.editor.chart.editText', { text: targets.value[box.target]?.text ?? '' })

// ------------------------------------------------------------ lifecycle

let offRender = null
let resize = null
onMounted(() => {
  offRender = props.controller.onRender(measure)
  resize = new ResizeObserver(measure)
  resize.observe(props.controller.block())
  props.controller.block().addEventListener('mousedown', onChartPointer)
  window.document.addEventListener('pointerdown', onOutsidePointer, true)
  requestAnimationFrame(() => {
    expanded.value = true
    measure()
    sheet.value?.focus()
  })
})

onBeforeUnmount(() => {
  unsubscribe()
  offRender?.()
  resize?.disconnect()
  props.controller.block().removeEventListener('mousedown', onChartPointer)
  window.document.removeEventListener('pointerdown', onOutsidePointer, true)
  props.controller.preview(undefined)
})

const cellLabel = (row, col) => {
  const column = columns.value[col]
  const rowLabel = rows.value[row]?.label ?? String(row + 1)
  return column?.leading ? `${column.label} ${row + 1}` : t('messages.editor.chart.cellLabel', { series: column?.label ?? '', category: rowLabel })
}

const toolClass = 'flex h-7 cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-2 text-[13px] text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40'
</script>

<template>
  <Teleport :to="controller.host()">
    <!-- The chart's texts, edited where they are drawn. -->
    <div class="studio-chart-targets" data-testid="studio-chart-targets">
      <button
        v-for="box in boxes"
        v-show="editingText?.target !== box.target"
        :key="box.target"
        type="button"
        class="studio-chart-target"
        :style="boxStyle(box)"
        :aria-label="targetLabel(box)"
        :data-testid="`studio-chart-target-${box.target}`"
        @click="openText(box)"
      />
      <textarea
        v-if="editingText"
        ref="textInput"
        :value="editingText.text"
        :rows="textRows"
        class="studio-chart-text-field"
        :style="textFieldStyle"
        :aria-label="targetLabel(editingText.box)"
        data-testid="studio-chart-text-field"
        @input="onTextInput"
        @keydown="onTextKeydown"
        @blur="finishText(true)"
      />
    </div>

    <div class="studio-chart-edit-panel" :class="{ 'is-open': expanded }" data-testid="studio-chart-editor" @keydown="onPanelKeydown">
      <div class="overflow-hidden">
        <div class="flex flex-col gap-2 pt-3 pb-1 font-sans">
          <div class="flex flex-wrap items-center gap-0.5" role="toolbar" :aria-label="t('messages.editor.chart.toolbar')">
            <button type="button" :class="toolClass" data-testid="studio-chart-add-row" @click="addRowBelow">
              <span class="pi pi-plus text-[11px]" aria-hidden="true" />{{ t('messages.editor.chart.addRow') }}
            </button>
            <button type="button" :class="toolClass" :disabled="!canRemoveRow" data-testid="studio-chart-remove-row" @click="removeActiveRow">
              <span class="pi pi-minus text-[11px]" aria-hidden="true" />{{ t('messages.editor.chart.removeRow') }}
            </button>
            <span class="mx-1 h-4 w-px bg-gray-200" aria-hidden="true" />
            <button type="button" :class="toolClass" data-testid="studio-chart-add-series" @click="addSeriesAfter">
              <span class="pi pi-plus text-[11px]" aria-hidden="true" />{{ t('messages.editor.chart.addSeries') }}
            </button>
            <button type="button" :class="toolClass" :disabled="!canRemoveActiveSeries" data-testid="studio-chart-remove-series" @click="removeActiveSeries">
              <span class="pi pi-minus text-[11px]" aria-hidden="true" />{{ t('messages.editor.chart.removeSeries') }}
            </button>
            <Button type="button" size="small" class="ml-auto" :label="t('messages.editor.chart.done')" data-testid="studio-chart-done" @click="done" />
          </div>
          <div v-if="linkedNote" class="flex items-center gap-1.5 text-[13px] text-gray-500" data-testid="studio-chart-linked-note">
            <span class="pi pi-link text-[11px]" aria-hidden="true" />{{ t('messages.editor.chart.linkedNote') }}
          </div>
          <StudioDataSheet
            ref="sheet"
            :columns="columns"
            :rows="rows"
            :label="t('messages.editor.chart.sheet')"
            :cell-label="cellLabel"
            @draft="onDraft"
            @input="onInput"
            @header="onHeader"
            @select="cell => (active = cell)"
            @undo="undo"
            @redo="redo"
          />
          <div class="min-h-5 text-[13px]" :class="status ? 'text-red-700' : 'invisible'" role="status" data-testid="studio-chart-invalid">
            {{ status }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
